import { LoginBlock } from "@sylviq/ui-react-web/blocks/auth/LoginBlock";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { MonochromeMark } from "@sylviq/ui-react-web/components/ui/MonochromeMark";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { BrandLockup } from "@/features/marketing/components/BrandLockup";
import { RememberMe } from "@/features/auth/components/RememberMe";
import { TurnstileChallenge } from "@/features/auth/components/TurnstileChallenge";
import {
  ApiError,
  isMfaChallenge,
  login,
  requestPasswordReset,
  resendVerification,
  signup,
  verifyTotp,
} from "@/lib/api";
import { useDocumentMeta } from "@/lib/document-meta";
import { clearHint, writeHint } from "@/lib/remember";
import { followPostLoginContinuation } from "@/lib/return-to";
import { useSession } from "@/lib/session";

type Mode = "login" | "signup";

export function AuthPage({
  initialMode = "login",
}: {
  initialMode?: Mode;
}) {
  const [mode, setModeState] = useState<Mode>(initialMode);
  const [error, setError] = useState<string | null>(null);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [signupTurnstileToken, setSignupTurnstileToken] = useState<
    string | null
  >(null);
  const [signupChallengeAttempt, setSignupChallengeAttempt] = useState(0);
  // Login-step-only opt-in. This lives in page state (not in LoginBlock's
  // form) so it survives the MFA/TOTP-verify step that follows a password
  // check — the hint is written only after the session actually exists.
  const [rememberMe, setRememberMe] = useState(false);
  // Email from the password step, held for the hint write after MFA verify.
  const [pendingLoginEmail, setPendingLoginEmail] = useState<string | null>(
    null,
  );
  // Address awaiting confirmation: set after signup, or when a sign-in is
  // refused because the address has not been confirmed yet.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  // Forgot-password step: null = not shown, otherwise whether the request has
  // been sent (the API answers the same for every address).
  const [forgot, setForgot] = useState<"form" | "sent" | null>(null);
  const { refresh } = useSession();
  const navigate = useNavigate();

  const setMode = (next: Mode) => {
    setModeState(next);
    const url = new URL(window.location.href);
    url.pathname = next === "signup" ? "/signup" : "/login";
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const afterAuth = async () => {
    await refresh();
    if (!followPostLoginContinuation(window.location.search)) {
      void navigate({ to: "/home" });
    }
  };

  const handleLogin = async (credentials: {
    email: string;
    password: string;
  }) => {
    setError(null);
    setForgotMessage(null);
    setPending(true);
    try {
      const result = await login(credentials, { rememberMe });
      if (isMfaChallenge(result)) {
        setPendingLoginEmail(credentials.email);
        setMfaToken(result.mfa_token);
        return;
      }
      persistLoginHint(credentials.email, rememberMe);
      await afterAuth();
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "email_not_verified") {
        setUnverifiedEmail(credentials.email);
        return;
      }
      setError(describeAuthError(cause, "Sign in"));
    } finally {
      setPending(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaToken) return;
    setError(null);
    setPending(true);
    try {
      await verifyTotp({ mfa_token: mfaToken, code: mfaCode });
      if (pendingLoginEmail) persistLoginHint(pendingLoginEmail, rememberMe);
      else if (!rememberMe) clearHint();
      setPendingLoginEmail(null);
      await afterAuth();
    } catch (cause) {
      setError(describeAuthError(cause, "Verification"));
    } finally {
      setPending(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaToken(null);
    setMfaCode("");
    setPendingLoginEmail(null);
    setError(null);
  };

  const handleSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setError(null);
    setForgotMessage(null);
    setPending(true);
    try {
      const email = String(data.get("email") ?? "");
      await signup(
        {
          email,
          username: String(data.get("username") ?? ""),
          password: String(data.get("password") ?? ""),
        },
        signupTurnstileToken ?? undefined,
      );
      setUnverifiedEmail(email);
    } catch (cause) {
      setError(describeAuthError(cause, "Create account"));
      // Turnstile tokens are single-use. A failed attempt still consumes the
      // token, so remount the widget before the person retries.
      setSignupTurnstileToken(null);
      setSignupChallengeAttempt((value) => value + 1);
    } finally {
      setPending(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setError(null);
    setPending(true);
    try {
      await resendVerification(unverifiedEmail);
      setResent(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 429
          ? "Too many requests. Wait a while before asking for another email."
          : "Couldn't send the email. Please try again.",
      );
    } finally {
      setPending(false);
    }
  };

  const handleForgot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get("email") ?? "");
    setError(null);
    setPending(true);
    try {
      await requestPasswordReset(email);
      setForgot("sent");
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 429
          ? "Too many requests. Wait a while before asking for another email."
          : "Couldn't send the email. Please try again.",
      );
    } finally {
      setPending(false);
    }
  };

  const handleBackFromVerify = () => {
    setUnverifiedEmail(null);
    setResent(false);
    setError(null);
    setMode("login");
  };

  const stepHeading = forgot
    ? "Reset your password"
    : unverifiedEmail
      ? "Confirm your email"
      : mfaToken
        ? "Two-factor verification"
        : mode === "login"
          ? "Sign in to SYLVIQ"
          : "Create your SYLVIQ account";

  useDocumentMeta({
    title: `${stepHeading} | SYLVIQ`,
    description:
      "Sign in to SYLVIQ or create an account to open your organization and apps.",
    path: mode === "signup" ? "/signup" : "/login",
  });

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-4 px-6 py-16">
      <h1 className="sr-only">{stepHeading}</h1>
      {forgot ? (
        <div className="flex flex-col gap-6">
          <BrandMark />
          <Card>
            <CardHeader className="justify-center text-center">
              <div>
                <CardTitle className="text-xl">Reset your password</CardTitle>
                <CardDescription>
                  {forgot === "sent"
                    ? "If that address has an account, a reset link is on its way. It works once and expires in an hour."
                    : "Enter your account email and we'll send you a reset link."}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-5"
                aria-busy={pending}
                onSubmit={handleForgot}
              >
                {forgot === "form" ? (
                  <>
                    <label
                      className="grid gap-2 text-sm"
                      htmlFor="forgot-email"
                    >
                      Email
                      <input
                        autoComplete="email"
                        className="input"
                        disabled={pending}
                        id="forgot-email"
                        name="email"
                        placeholder="you@example.com"
                        required
                        type="email"
                      />
                    </label>
                    <Button className="w-full" disabled={pending} type="submit">
                      {pending ? "Sending..." : "Send reset link"}
                    </Button>
                  </>
                ) : null}
                <p className="text-center text-xs text-[var(--muted-foreground)]">
                  <button
                    className="font-medium text-[var(--action)] hover:underline"
                    type="button"
                    onClick={() => {
                      setForgot(null);
                      setError(null);
                    }}
                  >
                    Back to sign in
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : unverifiedEmail ? (
        <div className="flex flex-col gap-6">
          <BrandMark />
          <Card>
            <CardHeader className="justify-center text-center">
              <div>
                <CardTitle className="text-xl">Confirm your email</CardTitle>
                <CardDescription>
                  We sent a confirmation link to {unverifiedEmail}. Open it to
                  finish setting up your account, then sign in. The link works
                  once and expires in 24 hours.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Button
                className="w-full"
                type="button"
                variant="outline"
                disabled={pending || resent}
                onClick={() => void handleResend()}
              >
                {resent ? "Email sent" : "Send it again"}
              </Button>
              <p className="text-center text-xs text-[var(--muted-foreground)]">
                Nothing arrived? Check spam, or{" "}
                <button
                  className="font-medium text-[var(--action)] hover:underline"
                  type="button"
                  onClick={handleBackFromVerify}
                >
                  back to sign in
                </button>
                .
              </p>
            </CardContent>
          </Card>
        </div>
      ) : mfaToken ? (
        <div className="flex flex-col gap-6">
          <BrandMark />
          <Card>
            <CardHeader className="justify-center text-center">
              <div>
                <CardTitle className="text-xl">
                  Check your authenticator app
                </CardTitle>
                <CardDescription>
                  Enter the 6-digit code from your authenticator app, or one of
                  your recovery codes.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-5"
                aria-busy={pending}
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleVerifyMfa();
                }}
              >
                <label className="grid gap-2 text-sm" htmlFor="mfa-code">
                  Verification code
                  <Input
                    autoFocus
                    autoComplete="one-time-code"
                    disabled={pending}
                    id="mfa-code"
                    inputMode="numeric"
                    maxLength={64}
                    name="code"
                    placeholder="123456"
                    required
                    value={mfaCode}
                    onChange={(event) =>
                      setMfaCode(event.target.value.replace(/\s+/g, ""))
                    }
                  />
                </label>
                <Button
                  className="w-full"
                  type="submit"
                  disabled={pending || mfaCode.trim().length === 0}
                >
                  {pending ? "Verifying..." : "Verify"}
                </Button>
                <p className="text-center text-xs text-[var(--muted-foreground)]">
                  Lost your device?{" "}
                  <button
                    className="font-medium text-[var(--action)] hover:underline disabled:opacity-50"
                    type="button"
                    disabled={pending}
                    onClick={handleBackToLogin}
                  >
                    Back to sign in
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : mode === "login" ? (
        // LoginBlock is owned by the @sylviq/ui-react-web package and takes no pending
        // prop, so the fieldset carries the disabled state during sign-in —
        // native behavior disables every inner input/button at once and
        // prevents double submits.
        <fieldset
          className="m-0 min-w-0 border-0 p-0"
          disabled={pending}
          aria-busy={pending}
        >
          <LoginBlock
            brand="SYLVIQ"
            mark={<MonochromeMark className="h-8 w-8" label={null} src="/brand/sylviq-mark-3.svg" />}
            onSubmit={(credentials) => void handleLogin(credentials)}
            onSignUp={() => {
              setError(null);
              setForgotMessage(null);
              setMode("signup");
            }}
            onForgotPassword={() => {
              setError(null);
              setForgotMessage(null);
              setForgot("form");
            }}
          />
          <div className="mt-4 flex justify-start">
            <RememberMe
              checked={rememberMe}
              disabled={pending}
              onChange={setRememberMe}
            />
          </div>
          {pending ? (
            <output className="mt-4 text-center text-xs text-[var(--muted-foreground)]">
              Signing in...
            </output>
          ) : null}
        </fieldset>
      ) : (
        <div className="flex flex-col gap-6">
          <BrandMark />
          <Card>
            <CardHeader className="justify-center text-center">
              <div>
                <CardTitle className="text-xl">Create your account</CardTitle>
                <CardDescription>
                  One account, every app — start with Accounting, built with
                  the accountants at Niche Accounting.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-5"
                aria-busy={pending}
                onSubmit={handleSignup}
              >
                <label className="grid gap-2 text-sm" htmlFor="signup-username">
                  Username
                  <input
                    autoComplete="username"
                    className="input"
                    disabled={pending}
                    id="signup-username"
                    maxLength={32}
                    minLength={3}
                    name="username"
                    required
                    type="text"
                  />
                </label>
                <label className="grid gap-2 text-sm" htmlFor="signup-email">
                  Email
                  <input
                    autoComplete="email"
                    className="input"
                    disabled={pending}
                    id="signup-email"
                    name="email"
                    placeholder="you@example.com"
                    required
                    type="email"
                  />
                </label>
                <label className="grid gap-2 text-sm" htmlFor="signup-password">
                  Password
                  <input
                    autoComplete="new-password"
                    className="input"
                    disabled={pending}
                    id="signup-password"
                    minLength={12}
                    name="password"
                    required
                    type="password"
                  />
                  <span className="text-xs text-[var(--muted-foreground)]">
                    At least 12 characters.
                  </span>
                </label>
                <TurnstileChallenge
                  key={signupChallengeAttempt}
                  action="signup"
                  onToken={setSignupTurnstileToken}
                />
                <Button
                  disabled={
                    pending ||
                    (Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY) &&
                      !signupTurnstileToken)
                  }
                  className="w-full"
                  type="submit"
                >
                  {pending ? "Creating account..." : "Create account"}
                </Button>
                <p className="text-center text-xs text-[var(--muted-foreground)]">
                  Already have an account?{" "}
                  <button
                    className="font-medium text-[var(--action)] hover:underline disabled:opacity-50"
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      setError(null);
                      setForgotMessage(null);
                      setMode("login");
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      {forgotMessage && !mfaToken && !unverifiedEmail && !forgot ? (
        <output className="text-center text-xs text-[var(--muted-foreground)]">
          {forgotMessage}
        </output>
      ) : null}
      {error ? (
        <p role="alert" className="text-center text-xs text-[var(--danger)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Writes the login hint on opt-in, clears it on opt-out. No expiry — the
 * hint persists until an explicit Forget; only an explicit opt-out clears it
 * here, never a timeout. */
function persistLoginHint(email: string, checked: boolean): void {
  if (checked) {
    writeHint(email);
    return;
  }
  clearHint();
}

/** The three-row mark, reused by the signup, MFA, reset and confirm steps so
 * every step of the auth flow shares one visual identity. */
function BrandMark() {
  return (
    <div className="flex items-center justify-center">
      <BrandLockup className="h-14 w-14" />
    </div>
  );
}

function describeAuthError(cause: unknown, action: string): string {
  if (cause instanceof ApiError) {
    if (cause.status === 429)
      return "Too many attempts. Wait a moment and try again.";
    if (cause.status === 400 || cause.status === 422) {
      if (action === "Verification")
        return "That code doesn't look right. Check your authenticator app and try again.";
      return "Check your email, username, and password (12+ characters) and try again.";
    }
    if (cause.status === 401) {
      if (action === "Verification")
        return "Invalid code. Check your authenticator app and try again.";
      if (action === "Create account")
        return "Account creation failed. Please try again.";
      return "Incorrect email or password.";
    }
    if (cause.status === 409) {
      if (action === "Create account")
        return "That email or username is already taken.";
      return `${action} failed. Please try again.`;
    }
  }
  return `${action} failed. Please try again.`;
}
